
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Gift, Menu, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

export function HandcraftedPersonalTouchHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-[#F8F5F2] text-[#5A5A5A] font-serif">
      <header className="container mx-auto px-6 py-5 flex justify-between items-center border-b border-[#EAE5E0]">
        <h2 className="text-xl md:text-2xl italic">The Potter's Kiln</h2>
        <nav className="hidden md:flex gap-8 items-center text-sm">
          <a href="#" className="hover:text-[#8A7A6A]">
            Shop
          </a>
          <a href="#" className="hover:text-[#8A7A6A]">
            Workshops
          </a>
          <a href="#" className="hover:text-[#8A7A6A]">
            Our Story
          </a>
          <Button variant="ghost" className="hover:bg-[#EAE5E0]">
            Cart (0)
          </Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-[#F8F5F2]/95 border-b">
          <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg">
            <a href="#" className="hover:text-[#8A7A6A]">Shop</a>
            <a href="#" className="hover:text-[#8A7A6A]">Workshops</a>
            <a href="#" className="hover:text-[#8A7A6A]">Our Story</a>
            <Button variant="outline" className="w-full mt-2 border-[#D9CBC1]">
                Cart (0)
            </Button>
          </nav>
        </div>
      )}

      <main>
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-5xl md:text-6xl leading-tight text-[#4A4A4A]">
                Made by hand,
                <br />
                with heart.
              </h1>
              <p className="mt-6 text-lg max-w-md mx-auto md:mx-0">
                Discover unique, small-batch ceramics, crafted with care in our
                little studio. Each piece tells a story.
              </p>
              <Button
                size="lg"
                className="mt-8 bg-[#8A7A6A] text-white hover:bg-[#7A6A5A] rounded-full px-8"
              >
                Explore the collection
              </Button>
            </div>
            <div className="relative h-[50vh] md:h-[60vh] w-full">
              <Image
                src="https://picsum.photos/seed/handcraft1/1000/1200"
                alt="A potter's hands shaping clay on a wheel"
                fill
                className="object-cover rounded-lg shadow-md"
                priority
                data-ai-hint="potter hands"
              />
            </div>
          </div>
        </section>

        <section className="py-24 bg-white px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl text-[#4A4A4A] mb-4">
                New from the Studio
              </h2>
              <p className="text-lg">
                Freshly fired and ready for a new home.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
              <Card className="border-[#EAE5E0] shadow-sm hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <Image
                    src="https://picsum.photos/seed/pottery1/600/600"
                    width={600}
                    height={600}
                    alt="A set of rustic ceramic mugs"
                    className="rounded-md mb-4"
                    data-ai-hint="ceramic mugs"
                  />
                  <h3 className="font-bold text-lg text-[#3A3A3A]">
                    Morning Ritual Mugs
                  </h3>
                  <p className="mt-1">$42.00</p>
                </CardContent>
              </Card>
              <Card className="border-[#EAE5E0] shadow-sm hover:shadow-lg transition-shadow relative">
                <CardContent className="p-4">
                  <Badge
                    variant="outline"
                    className="absolute top-6 right-6 bg-white"
                  >
                    Best Seller
                  </Badge>
                  <Image
                    src="https://picsum.photos/seed/pottery2/600/600"
                    width={600}
                    height={600}
                    alt="A large, speckled ceramic serving bowl"
                    className="rounded-md mb-4"
                    data-ai-hint="serving bowl"
                  />
                  <h3 className="font-bold text-lg text-[#3A3A3A]">
                    Harvest Serving Bowl
                  </h3>
                  <p className="mt-1">$88.00</p>
                </CardContent>
              </Card>
              <Card className="border-[#EAE5E0] shadow-sm hover:shadow-lg transition-shadow sm:col-span-2 md:col-span-1">
                <CardContent className="p-4">
                  <Image
                    src="https://picsum.photos/seed/pottery3/600/600"
                    width={600}
                    height={600}
                    alt="Small, handmade ceramic planters"
                    className="rounded-md mb-4"
                    data-ai-hint="ceramic planters"
                  />
                  <h3 className="font-bold text-lg text-[#3A3A3A]">
                    Sprout Planters (Set of 3)
                  </h3>
                  <p className="mt-1">$65.00</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto text-center">
            <div className="max-w-3xl mx-auto">
              <Heart className="w-12 h-12 text-[#B9A999] mx-auto mb-4" />
              <h2 className="text-4xl text-[#4A4A4A] mb-4">
                Perfectly Imperfect
              </h2>
              <p className="text-lg leading-relaxed">
                We embrace the wabi-sabi philosophy, finding beauty in the
                slight variations and unique marks that make each handcrafted
                item one-of-a-kind. Your piece will be as individual as you are.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#EAE5E0] py-20 px-4">
          <div className="container mx-auto flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
            <Gift className="w-12 h-12 text-[#8A7A6A] shrink-0" />
            <div>
              <h3 className="text-2xl font-bold text-[#4A4A4A]">
                Looking for a special gift?
              </h3>
              <p className="mt-1">
                We offer gift wrapping and can include a handwritten note with
                your order.
              </p>
            </div>
            <Button className="bg-white text-[#8A7A6A] border border-[#D9CBC1] hover:bg-[#F8F5F2] shadow-sm shrink-0">
              Add Gift Options
            </Button>
          </div>
        </section>
      </main>
      <footer className="bg-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h3 className="text-3xl italic mb-4 text-[#4A4A4A]">Stay in touch</h3>
          <p className="max-w-xl mx-auto mb-8 text-gray-500">
            Be the first to know about new collections and studio events.
          </p>
          <div className="flex justify-center">
            <input
              type="email"
              placeholder="your.email@example.com"
              className="w-full max-w-sm bg-[#F8F5F2] border border-[#EAE5E0] p-3 placeholder-gray-400"
            />
            <button className="bg-[#8A7A6A] text-white p-3">Subscribe</button>
          </div>
          <div className="mt-16 pt-8 border-t border-[#EAE5E0] text-sm text-gray-400">
            <p>
              &copy; {new Date().getFullYear()} The Potter's Kiln. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
